// Lane G — SoP links + required attachments (backend djangoapp/urls_sop.py, mounted under /bp/).
import Axios from 'axios';

const bp = '/bp';

const plantId = () => {
  try {
    const p = JSON.parse(localStorage.getItem('active-plant') || 'null');
    return p?.id || null;
  } catch (e) {
    return null;
  }
};

const headers = (multipart = false) => ({
  Authorization: `Bearer ${localStorage.getItem('Token')}`,
  ...(multipart ? {} : { 'Content-Type': 'application/json' }),
  Accept: 'application/json',
});

const unwrap = (p) =>
  p.then((r) => r.data).catch((e) => e?.response?.data || { status: 'failed', message: e?.message || 'Network error' });

const get = (url, params = {}) => unwrap(Axios.get(`${bp}${url}`, { params: { plant_id: plantId(), ...params }, headers: headers() }));
const post = (url, body = {}) => unwrap(Axios.post(`${bp}${url}`, { plant_id: plantId(), ...body }, { headers: headers() }));
const put = (url, body = {}) => unwrap(Axios.put(`${bp}${url}`, body, { headers: headers() }));

export const sopApi = {
  // SP1 master
  list: (params) => get('/sop/', params),
  detail: (id) => get(`/sop/${id}/`),
  create: (body) => post('/sop/', body),
  update: (id, body) => put(`/sop/${id}/`, body),
  makeEffective: (id, body) => post(`/sop/${id}/effective/`, body),
  retire: (id, reason) => post(`/sop/${id}/retire/`, { reason }),
  newVersion: (id, body) => post(`/sop/${id}/new-version/`, body),
  uploadFile: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(Axios.post(`${bp}/sop/${id}/upload/`, fd, { headers: headers(true) }));
  },
  importRows: (rows) => post('/sop/import/', { rows }),
  syncDms: (search = '') => post('/sop/sync-dms/', { search }),
  vocab: () => get('/sop/object-types/'),
  // SP2 links
  linksFor: (object_type, object_ref) => get('/sop/for/', { object_type, object_ref }),
  linksOfSop: (sop_no) => get('/sop/links/', { sop_no }),
  addLink: (body) => post('/sop/links/', body),
  removeLink: (link_id, reason) => post(`/sop/links/${link_id}/remove/`, { reason }),
  // SP3 / SP4
  frozen: (batch_number, operation_id) => get('/sop/frozen/', { batch_number, operation_id }),
  gate: (operation_id, batch_number) => get('/sop/gate/', { operation_id, batch_number }),
  changed: (recipe_id, since) => get('/sop/changed/', { recipe_id, since }),
  // X1 / X2
  rules: (params) => get('/sop/attachment-rules/', params),
  createRule: (body) => post('/sop/attachment-rules/', body),
  updateRule: (id, body) => put(`/sop/attachment-rules/${id}/`, body),
  deactivateRule: (id) => post(`/sop/attachment-rules/${id}/deactivate/`, {}),
  attachmentStatus: (batch_number, operation_id) => get('/sop/attachments/status/', { batch_number, operation_id }),
  // X7 — the batch-wide numbered attachment index (djangoapp/views/Batch/execution_hardening_view.py::attachments_index,
  // routed at djangoapp/urls.py:543). Answers { index, complete, missing, required }: `index` is every
  // BatchAttachment *and* AdditionalRecord of the batch in record-number order, voids included and marked.
  // `recipe_id` is optional server-side, but completeness/`required` are only computed when it is supplied —
  // without it the endpoint answers complete:true with an empty requirement list, so callers must not read
  // that as release evidence (see BatchAttachmentsIndex, which labels the difference).
  attachmentsIndex: (batch_number, recipe_id) =>
    get('/execution/attachments/', { batch_number, ...(recipe_id ? { recipe_id } : {}) }),
  uploadAttachment: ({ file, batch_number, operation_id, kind, rule_code, requirement_id, instrument_id }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('batch_number', batch_number);
    fd.append('operation_id', operation_id);
    if (kind) fd.append('kind', kind);
    if (rule_code) fd.append('rule_code', rule_code);
    if (requirement_id) fd.append('requirement_id', requirement_id);
    if (instrument_id) fd.append('instrument_id', instrument_id);
    if (plantId()) fd.append('plant_id', plantId());
    return unwrap(Axios.post(`${bp}/sop/attachments/upload/`, fd, { headers: headers(true) }));
  },
};

// The API answers `status: true` (operator-app contract); older callers may still see 'success'.
export const isOk = (r) => r?.status === true || r?.status === 'success' || r?.ok === true;

export const sopLabel = (s) => (s ? `${s.sop_no || s.doc_no} v${s.version ?? s.current_version ?? '?'}` : '');

// SOP files are streamed behind auth (`/bp/sop/<id>/file/`): fetch with the bearer token and hand back an object URL.
export const fetchSopBlobUrl = async (fileUrl) => {
  const r = await fetch(fileUrl, { headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });
  if (!r.ok) throw new Error(`File not available (${r.status})`);
  return URL.createObjectURL(await r.blob());
};

export const openSop = async (s) => {
  const fileUrl = s?.file_url || s?.current?.file_url;
  const url = s?.url || s?.current?.url;
  if (fileUrl) {
    try {
      const blobUrl = await fetchSopBlobUrl(fileUrl);
      window.open(blobUrl, '_blank', 'noopener');
      return;
    } catch (e) {
      if (!url) throw e;
    }
  }
  if (url) window.open(url, '_blank', 'noopener');
};

export default sopApi;
